// utils/deviceUtils.js (Updated with Instagram detection)

// 카카오 인앱 브라우저 감지
export const isKakaoInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return userAgent.includes('KAKAOTALK') ||
        userAgent.includes('KakaoTalk') ||
        userAgent.includes('KAKAO');
};

// 인스타그램 인앱 브라우저 감지
export const isInstagramInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return userAgent.includes('Instagram') ||
        userAgent.includes('FBAN') ||
        userAgent.includes('FBAV') ||
        (userAgent.includes('Mobile/') && userAgent.includes('Instagram'));
};

// 페이스북 인앱 브라우저 감지 (보너스)
export const isFacebookInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return (userAgent.includes('FBAN') || userAgent.includes('FBAV')) &&
        !userAgent.includes('Instagram');
};

// 네이버 인앱 브라우저 감지 (보너스)
export const isNaverInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return userAgent.includes('NAVER') ||
        userAgent.includes('Line/');
};

// 통합 인앱 브라우저 감지
export const getInAppBrowserType = () => {
    if (isKakaoInAppBrowser()) return 'kakao';
    if (isInstagramInAppBrowser()) return 'instagram';
    if (isFacebookInAppBrowser()) return 'facebook';
    if (isNaverInAppBrowser()) return 'naver';
    return null;
};

// 인앱 브라우저 여부 확인
export const isAnyInAppBrowser = () => {
    return getInAppBrowserType() !== null;
};

// 모바일 기기 여부 확인
export const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPhone|iPad|iPod|Android/i.test(userAgent);
};

// 디바이스 정보 가져오기
export const getDeviceInfo = (userAgent) => {
    if (!userAgent) return '정보 없음';

    // 인앱 브라우저 정보 포함
    const browserType = getInAppBrowserType();
    if (browserType) {
        const browserNames = {
            kakao: '카카오톡',
            instagram: '인스타그램',
            facebook: '페이스북',
            naver: '네이버'
        };
        return `📱 ${browserNames[browserType]} 앱`;
    }

    // 일반 브라우저/디바이스 정보
    if (userAgent.includes('iPhone')) return '📱 iPhone';
    if (userAgent.includes('Android')) return '📱 Android';
    if (userAgent.includes('iPad')) return '📱 iPad';
    if (userAgent.includes('Windows')) return '💻 Windows';
    if (userAgent.includes('Mac')) return '💻 Mac';
    return '💻 PC';
};

// 디바이스 ID 생성
export const generateDeviceId = () => {
    try {
        let deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
            const fingerprint = [
                navigator.userAgent,
                navigator.language,
                navigator.platform,
                window.screen.width + 'x' + window.screen.height,
                window.screen.colorDepth,
                new Date().getTimezoneOffset(),
                navigator.hardwareConcurrency || 'unknown',
                navigator.deviceMemory || 'unknown',
                Date.now().toString(),
                Math.random().toString(36)
            ].join('|');

            // 해시 생성
            let hash = 0;
            for (let i = 0; i < fingerprint.length; i++) {
                const char = fingerprint.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }

            deviceId = Math.abs(hash).toString(36).toUpperCase().slice(0, 16);
            localStorage.setItem('deviceId', deviceId);

            // GA4: DeviceID 생성 이벤트 전송
            try {
                const gtag = window.gtag;
                const measurementId = window.GA_MEASUREMENT_ID;
                if (typeof gtag === 'function' && measurementId) {
                    gtag('event', 'device_id_created', {
                        device_id: deviceId,
                        device_type: getDeviceInfo(navigator.userAgent),
                        is_mobile: isMobile(),
                        is_in_app_browser: isAnyInAppBrowser(),
                        in_app_browser_type: getInAppBrowserType() || 'none',
                        screen_resolution: `${window.screen.width}x${window.screen.height}`,
                        platform: navigator.platform,
                        language: navigator.language
                    });
                }
            } catch (gaError) {
                console.warn('GA4 이벤트 전송 실패:', gaError);
            }
        }
        return deviceId;
    } catch (error) {
        const randomId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
        const fallbackDeviceId = randomId.toUpperCase().slice(0, 16);
        
        // 에러 발생 시에도 GA4 이벤트 전송 시도
        try {
            const gtag = window.gtag;
            const measurementId = window.GA_MEASUREMENT_ID;
            if (typeof gtag === 'function' && measurementId) {
                gtag('event', 'device_id_created', {
                    device_id: fallbackDeviceId,
                    device_type: 'unknown',
                    is_mobile: isMobile(),
                    is_in_app_browser: isAnyInAppBrowser(),
                    in_app_browser_type: getInAppBrowserType() || 'none',
                    error: true,
                    error_message: error.message || 'unknown_error'
                });
            }
        } catch (gaError) {
            console.warn('GA4 이벤트 전송 실패:', gaError);
        }
        
        return fallbackDeviceId;
    }
};

// URL 복사 함수
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        alert('주소가 복사되었습니다!');
    } catch (error) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('주소가 복사되었습니다!');
    }
};

// 인앱 브라우저별 외부 브라우저로 열기 방법
export const openInExternalBrowser = (browserType) => {
    const url = window.location.href;

    switch (browserType) {
        case 'kakao':
            // 카카오톡 외부 브라우저로 열기
            window.location.href = 'kakaotalk://web/openExternal?url=' + encodeURIComponent(url);
            break;

        case 'instagram':
            // 인스타그램은 외부 브라우저로 직접 열기가 제한적
            // 사용자에게 URL 복사 안내
            copyToClipboard(url);
            break;

        case 'facebook':
            // 페이스북도 제한적이므로 URL 복사
            copyToClipboard(url);
            break;

        case 'naver':
            // 네이버 라인 외부 브라우저로 열기
            copyToClipboard(url);
            break;

        default:
            copyToClipboard(url);
    }
};

// 크롬 브라우저로 열기 시도
export const openInChrome = () => {
    const url = window.location.hostname;
    const chromeIntent = `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;

    try {
        window.location.href = chromeIntent;
        setTimeout(() => {
            alert('Chrome 앱이 설치되어 있지 않거나 자동 이동이 되지 않는 경우:\n\n1. Chrome 앱을 실행하세요\n2. 주소창에 ' + url + ' 을 입력하세요\n3. 또는 복사한 주소를 붙여넣기 하세요');
        }, 2000);
    } catch (error) {
        alert('Chrome 앱을 실행하여 ' + url + ' 을 방문해주세요');
    }
};

// 인앱 브라우저 닫기
export const closeInAppBrowser = (browserType) => {
    switch (browserType) {
        case 'kakao':
            window.location.href = 'kakaotalk://close';
            break;
        case 'instagram':
            // 인스타그램은 직접 닫기가 어려움
            window.history.back();
            break;
        case 'facebook':
            window.history.back();
            break;
        case 'naver':
            window.history.back();
            break;
        default:
            window.close();
    }

    setTimeout(() => {
        window.close();
    }, 500);
};

// 홈 화면에 추가 안내
export const handleAddToHomeScreen = () => {
    // iOS Safari
    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
        if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('CriOS')) {
            alert('홈 화면에 추가하려면:\n\n1. 하단의 공유 버튼(□↗)을 누르세요\n2. "홈 화면에 추가"를 선택하세요\n3. "추가"를 눌러 완료하세요');
            return;
        }
    }

    // Android Chrome
    if (navigator.userAgent.includes('Android')) {
        alert('홈 화면에 추가하려면:\n\n1. 우상단의 메뉴(⋮)를 누르세요\n2. "홈 화면에 추가"를 선택하세요\n3. "추가"를 눌러 완료하세요');
        return;
    }

    // 기타 브라우저
    alert('홈 화면에 추가하려면:\n\n브라우저 메뉴에서\n"홈 화면에 추가" 또는\n"바탕화면에 추가"를 찾아 선택하세요');
};

// PWA 설치 프롬프트 지원 (beforeinstallprompt 캐치)
let _deferredPrompt = null;
try {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        _deferredPrompt = e;
    });
} catch (_) { /* noop */ }

// 설치 완료 이벤트: 기기/브라우저에서 앱 설치가 완료되면 호출됨
// 배너 노출 제어를 위해 영속 플래그를 저장한다
try {
    window.addEventListener('appinstalled', () => {
        try {
            localStorage.setItem('pwaInstalled', 'true');
        } catch (_) { /* noop */ }
    });
} catch (_) { /* noop */ }

export const canPromptPwaInstall = () => {
    return !!_deferredPrompt;
};

export const promptPwaInstall = async () => {
    try {
        if (!_deferredPrompt) return { supported: false, outcome: 'dismissed' };
        _deferredPrompt.prompt();
        const choice = await _deferredPrompt.userChoice;
        _deferredPrompt = null;
        return { supported: true, outcome: choice.outcome };
    } catch (e) {
        return { supported: false, outcome: 'error' };
    }
};

// PWA로 설치되어 있는지 확인
export const isPWAInstalled = () => {
    // Android Chrome: display-mode가 standalone인지 확인
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    // iOS Safari: navigator.standalone 확인
    if (window.navigator.standalone === true) {
        return true;
    }
    // 사용자가 이전에 설치를 완료한 기록이 있는지 확인 (브라우저에서 열어도 배너 숨김)
    try {
        if (localStorage.getItem('pwaInstalled') === 'true') {
            return true;
        }
    } catch (_) { /* noop */ }
    return false;
};

// 금액 포맷팅
export const formatMoney = (amount) => {
    if (amount >= 100000000) {
        return Math.round(amount / 100000000) + '억원';
    } else if (amount >= 10000) {
        return Math.round(amount / 10000) + '만원';
    } else if (amount > 0) {
        return amount.toLocaleString() + '원';
    }
    return '미정';
};